import {
  useWSSnackbar,
  WSActions,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLog } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { queriesWorkLogs } from "@query";
import { WorkLogAttachmentsTable } from "../WorkLogAttachmentsTable";
import { useModalEditWorkLogNotes } from "../../modals/ModalEditWorkLogNotes/useModalEditWorkLogNotes";
import { useModalRemoveWorkLogNotes } from "../../modals/ModalRemoveWorkLogNotes/useModalRemoveWorkLogNotes";
import { useModalUploadWorkLogAttachment } from "../../modals/ModalUploadWorkLogAttachment/useModalUploadWorkLogAttachment";
import { canPayeeEdit } from "../../utils/workLogPermissions";
import { WorkLogAdditionalDetails } from "../WorkLogAdditionalDetails";
import styles from "../WorkLogAdditionalDetails/WorkLogAdditionalDetails.module.scss";

type Props = {
  workLog?: WorkLog;
  shimmer?: boolean;
  attachmentsRequired?: boolean;
};

export const WorkLogAdditionalDetailsEditable: React.FC<Props> = ({
  workLog,
  shimmer,
  attachmentsRequired
}) => {
  const { openSnackbar } = useWSSnackbar();
  const modalEditNotes = useModalEditWorkLogNotes();
  const modalRemoveNotes = useModalRemoveWorkLogNotes();
  const modalUploadAttachment = useModalUploadWorkLogAttachment();
  const [updateWorkLog] = queriesWorkLogs.updateWorkLog.useMutation();

  const isEditable =
    canPayeeEdit(workLog?.status) && !workLog?.convertedResourceId;
  const attachmentIds = workLog?.attachmentIds || [];
  const hasNotes = !!workLog?.notes;
  const hasAttachments = attachmentIds.length > 0;

  // Delegate to read-only component for shimmer and non-editable states
  if (shimmer || !isEditable) {
    return <WorkLogAdditionalDetails workLog={workLog} shimmer={shimmer} />;
  }

  const handleAddNotes = () => {
    if (workLog) {
      modalEditNotes.open({
        workLogId: workLog.id!,
        currentNotes: workLog.notes ?? undefined
      });
    }
  };

  const handleRemoveNotes = () => {
    if (workLog) {
      modalRemoveNotes.open({
        workLogId: workLog.id!
      });
    }
  };

  const handleAddAttachment = () => {
    if (workLog) {
      modalUploadAttachment.open({
        workLogId: workLog.id!,
        existingAttachmentIds: attachmentIds
      });
    }
  };

  const handleRemoveAttachment = (fileId: string) => {
    if (!workLog) return;

    const newAttachmentIds = attachmentIds.filter(id => id !== fileId);
    updateWorkLog(
      {
        workLogId: workLog.id!,
        workLogUpdate: { attachmentIds: new Set(newAttachmentIds) }
      },
      {
        onSuccess: () => {
          openSnackbar({
            icon: { name: "check" },
            message: "Attachment removed"
          });
        },
        onError: () => {
          openSnackbar({
            message: "Failed to remove attachment",
            type: "error"
          });
        }
      }
    );
  };

  if (!workLog) {
    return null;
  }

  return (
    <WSElement>
      <WSSectionToolbar title="Additional details" />
      <WSCard mt="M">
        {/* Notes Section */}
        <WSElement>
          <WSFlexBox.CenterY justify="space-between" mb="XS">
            <WSText.ParagraphSm color="gray500">
              Notes (optional)
            </WSText.ParagraphSm>
            <WSActions
              size="S"
              buttons={[
                {
                  kind: "Link",
                  label: hasNotes ? "Edit" : "Add",
                  onClick: handleAddNotes
                }
              ]}
              menuItems={
                hasNotes
                  ? [
                      { label: "Edit", icon: "edit", onClick: handleAddNotes },
                      { separator: true as const },
                      {
                        label: "Remove",
                        icon: "trash",
                        onClick: handleRemoveNotes,
                        destructive: true
                      }
                    ]
                  : undefined
              }
            />
          </WSFlexBox.CenterY>
          {hasNotes ? (
            <WSText.ParagraphSm color="gray700">
              {workLog.notes}
            </WSText.ParagraphSm>
          ) : (
            <WSElement className={styles.addLineItem}>
              <WSButton.Link icon="edit" onClick={handleAddNotes}>
                Add notes
              </WSButton.Link>
            </WSElement>
          )}
        </WSElement>

        {/* Attachments Section */}
        <WSElement mt="XL">
          <WSFlexBox.CenterY justify="space-between" mb="S">
            <WSText.ParagraphSm color="gray500">
              {attachmentsRequired ? "Attachments*" : "Attachments (optional)"}
            </WSText.ParagraphSm>
            <WSActions
              size="S"
              buttons={[
                { kind: "Link", label: "Add", onClick: handleAddAttachment }
              ]}
            />
          </WSFlexBox.CenterY>

          {hasAttachments ? (
            <WorkLogAttachmentsTable
              fileIds={attachmentIds}
              onRemove={handleRemoveAttachment}
            />
          ) : (
            <WSElement className={styles.addLineItem}>
              <WSButton.Link icon="paperclip" onClick={handleAddAttachment}>
                Add attachment
              </WSButton.Link>
            </WSElement>
          )}

          {attachmentsRequired && !hasAttachments && (
            <WSText.ParagraphSm color="red400" mt="S">
              At least one attachment is required to submit this work log.
            </WSText.ParagraphSm>
          )}
        </WSElement>
      </WSCard>
    </WSElement>
  );
};
